import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { UI } from "@/lib/i18n";

const I18nContext = createContext(null);

const STORAGE_KEY = "p2s_lang";

function detectLang() {
  if (typeof window === "undefined") return "en";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && ["en", "fr", "es", "pt"].includes(saved)) return saved;
  const nav = (window.navigator.language || "en").slice(0, 2).toLowerCase();
  if (["fr", "es", "pt"].includes(nav)) return nav;
  return "en";
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    setLangState(detectLang());
  }, []);

  const setLang = useCallback((next) => {
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) {}
    try { document.documentElement.setAttribute("lang", next); } catch (e) {}
  }, []);

  useEffect(() => {
    try { document.documentElement.setAttribute("lang", lang); } catch (e) {}
  }, [lang]);

  const t = useCallback(
    (key) => (UI[lang] && UI[lang][key]) || UI.en[key] || key,
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

// Pick the right language slice from a {en,fr,es,pt} dict
export function pickLang(dict, lang) {
  return dict[lang] || dict.en;
}
